import type { UserType } from "@/types/entities/users";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
} from "@elaniintc/goes-design-system";
import { useRegistryForm } from "../contexts/RegistryFormContext";
import { useCallback } from "react";
import type { EntityRoleType } from "@/schemas/digital-files";
import { UserApplicantSearchSelector } from "./applicant-search-selector";
import { getAssociate } from "../../utils";

export const UsersList = () => {
  const { users, rolesList, setUsers } = useRegistryForm();

  const handleAddUser = useCallback(
    (user: UserType) => {
      setUsers((prev) => [...prev, getAssociate(user)]);
    },
    [setUsers],
  );

  const handleRemoveUser = useCallback(
    (userId: string) => {
      setUsers((prev) => prev.filter((u) => u.applicant_id !== userId));
    },
    [setUsers],
  );

  const assignRole = (role: EntityRoleType, userId: string) => {
    setUsers((prev) =>
      prev.map((user) => ({
        ...user,
        ...(user.applicant_id === userId ? { role, role_id: role.id } : {}),
      })),
    );
  };
  return (
    <>
      <UserApplicantSearchSelector
        label="Usuarios externos asociados"
        labelClassName="text-size-body text-body-loud"
        selectedUser={users}
        onSelectUser={handleAddUser}
        showCount={users.length > 0}
        count={users.length}
      />
      <div className="flex flex-col gap-1">
        {users.map((user) => (
          <div
            key={user.applicant_id}
            className="flex items-center gap-2 py-2 px-3 border border-neutral-quiet rounded-lg"
          >
            <Avatar size="xs">
              <AvatarFallback>
                {user.applicant?.first_name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex items-center gap-2 flex-auto min-w-0">
              <span className="text-body-loud font-semibold truncate shrink-0 max-w-[40%]">
                {user.applicant?.first_name || ""}{" "}
                {user.applicant?.last_name || ""}
              </span>
              <span className="text-body-quiet truncate shrink min-w-0">
                {user.applicant?.email}
              </span>
            </div>
            {rolesList && rolesList.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="tertiary" size="xs">
                    {user.role?.name ?? "Seleccione un rol"}{" "}
                    <Icon type="fas" name="chevron-down" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="bottom"
                  align="end"
                  className={cn(
                    "max-h-37.25 min-w-20 rounded-lg border-neutral-silent text-body-loud font-semibold bg-neutral-object",
                  )}
                >
                  {rolesList.map((role) => (
                    <DropdownMenuItem
                      key={role.id}
                      onClick={() => assignRole(role, user.applicant_id)}
                    >
                      {role.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button
              variant="ghost"
              size="icon"
              aria-label="Eliminar usuario"
              onClick={() => handleRemoveUser(user.applicant_id)}
            >
              <Icon type="fas" name="times" className="text-body" />
            </Button>
          </div>
        ))}
      </div>
    </>
  );
};
